import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CalendarDays } from "lucide-react";
import { Field, FieldLabel } from "@akuna/ui";
import { DateFieldPopover } from "@/components/ui/date-field-popover";
import type { ReservationFormValues } from "./reservationForm";
import { nextCalendarDate } from "./reservationScheduleDefaults";

function toDateInput(date: Date | undefined) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromDateInput(value: string | null | undefined) {
  return value ? new Date(`${value}T12:00:00`) : undefined;
}

/** Datas da reserva. Os horários reais são registrados no check-in e check-out. */
export function ReservationSchedule({ form }: { form: UseFormReturn<ReservationFormValues> }) {
  const { t, i18n } = useTranslation("daycareHotel");
  const isHotel = form.watch("moduleType") === "hotel";
  const locale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";
  const fmt = (value: string) => (value ? new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(fromDateInput(value)) : "");

  const setDate = (dateField: "checkInDate" | "checkOutDate") => (date: Date | undefined) => {
    const value = toDateInput(date);
    form.setValue(dateField, value, { shouldValidate: true, shouldDirty: true });

    if (dateField === "checkInDate" && isHotel && value) {
      const checkOutDate = form.getValues("checkOutDate");
      if (!checkOutDate || checkOutDate <= value) {
        form.setValue("checkOutDate", nextCalendarDate(value), { shouldValidate: true, shouldDirty: true });
      }
    }
  };

  const dateTrigger = (value: string) => (
    <button
      type="button"
      aria-label={t("reservationDate")}
      data-testid="reservation-date-trigger"
      className="flex h-10 min-w-[9rem] flex-1 items-center gap-2 rounded-lg border border-border bg-background px-3 text-left text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <CalendarDays size={15} className="text-muted-foreground" />
      <span className={value ? "" : "text-muted-foreground"}>{value ? fmt(value) : t("selectDate")}</span>
    </button>
  );

  const dateField = (
    label: string,
    dateField: "checkInDate" | "checkOutDate",
  ) => (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <DateFieldPopover
        selected={fromDateInput(form.watch(dateField))}
        onSelect={setDate(dateField)}
        trigger={dateTrigger(form.watch(dateField) ?? "")}
      />
    </Field>
  );

  if (isHotel) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {dateField(t("checkIn"), "checkInDate")}
        {dateField(t("checkOut"), "checkOutDate")}
      </div>
    );
  }

  return dateField(t("reservationDate"), "checkInDate");
}
